import {
    getGetDroneUrl,
    getUpdateDroneStatusUrl,
    getUpdateDroneUrl
} from 'generatedTypes/asset/services/drone-controller';
import { useState } from 'react';
import { normalizePayload } from 'utils/normalizePayload';

import useFetch from 'hooks/useFetch';
import useRetry from 'hooks/useRetry';

import useTranslation from './hooks/useTranslation';

import { DRONES_API } from './constants';
import type {
    CreateDrone,
    DroneResponse,
    FetchDrone,
    UpdateDrone,
    UpdateDroneStatus
} from './types';

const useDrone = () => {
    const [isLoading, setIsLoading] = useState(false);
    const { authenticatedGet, authenticatedPost, authenticatedPut } = useFetch();
    const { withRetry, withRetryAndDuplicateCheck } = useRetry();
    const { CONNECTION_ERROR, DRONE_NAME_DUPLICATE_ERROR, SAVE_FAILED } = useTranslation();

    const fetchDrone: FetchDrone = async ({ id }) => {
        try {
            setIsLoading(true);
            const url = getGetDroneUrl(id);

            return await withRetry(
                () => authenticatedGet<DroneResponse>(url),
                CONNECTION_ERROR,
                () => fetchDrone({ id })
            );
        } finally {
            setIsLoading(false);
        }
    };

    const createDrone: CreateDrone = async ({ drone }) => {
        try {
            setIsLoading(true);

            return await withRetryAndDuplicateCheck(
                () =>
                    authenticatedPost<DroneResponse>(
                        DRONES_API,
                        JSON.stringify(normalizePayload({ ...drone }))
                    ),
                SAVE_FAILED,
                DRONE_NAME_DUPLICATE_ERROR,
                () => createDrone({ drone })
            );
        } finally {
            setIsLoading(false);
        }
    };

    const updateDrone: UpdateDrone = async ({ drone, id }) => {
        try {
            setIsLoading(true);
            const url = getUpdateDroneUrl(id);

            return await withRetry(
                () =>
                    authenticatedPut<DroneResponse>(
                        url,
                        JSON.stringify(normalizePayload({ ...drone }))
                    ),
                SAVE_FAILED,
                () => updateDrone({ drone, id })
            );
        } finally {
            setIsLoading(false);
        }
    };

    const updateDroneStatus: UpdateDroneStatus = async ({ id, status }) => {
        try {
            setIsLoading(true);
            const url = getUpdateDroneStatusUrl(id);

            return await withRetry(
                () => authenticatedPut<DroneResponse>(url, JSON.stringify(status)),
                SAVE_FAILED,
                () => updateDroneStatus({ id, status })
            );
        } finally {
            setIsLoading(false);
        }
    };

    return {
        createDrone,
        fetchDrone,
        isLoading,
        updateDrone,
        updateDroneStatus
    };
};

export default useDrone;
